const API_BASE = "https://mypage.syon.jp/";

/**
 * セッション切れ（401）時の共通処理。
 * ログイン情報を破棄してログイン画面へ戻す。
 * ※メールリンク系（verify-email / change-email / reset-password / 登録トークン）は
 *   ページ内でエラー表示する設計のため対象外（素のfetchを使う）。
 */
function handleUnauthorized() {
  ["access_token", "email", "customer_number", "customer_type", "login_triggered"].forEach(
    (key) => localStorage.removeItem(key)
  );
  window.location.replace("/");
}

/**
 * ログイン中APIの共通fetch。401ならログイン画面へリダイレクトする。
 * @param {*} url
 * @param {*} options
 * @returns
 */
async function authFetch(url, options) {
  const res = await fetch(url, options);
  if (res.status === 401) {
    handleUnauthorized();
    // 画面遷移中に呼び出し元の後続処理（res.json()等）を走らせない
    return new Promise(() => {});
  }
  return res;
}

  /**
   * データベースサーバ現在時刻取得
   * @returns 
   */
export async function fetchNow() {
  const res = await fetch(`${API_BASE}api/now`);
  const json = await res.json();
  return json;
}

/**
 * ユーザ登録
 * @param {*} data 
 * @returns 
 */
export async function fetchRegister(data) {
  const res = await fetch(`${API_BASE}api/register`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(data),
  });
  const json = await res.json();
  return json;
}

/**
 * ユーザ登録メールアドレスの認証
 * @param {*} token 
 * @returns 
 */
export async function fetchVerifyEmail(token, customer_type) {
  const res = await fetch(
    `${API_BASE}api/verify-email?token=${encodeURIComponent(token)}&customer_type=${encodeURIComponent(customer_type)}`
  );
  const json = await res.json();
  return json;
}

/**
 * captcha画像取得
 * @returns 
 */
export async function fetchGetCaptcha() {
  const res = await fetch(`${API_BASE}api/captcha`);
  const json = await res.json();
  return json;
}

/**
 * ログイン
 * @param {*} data 
 * @returns 
 */
export async function fetchLogin(data) {
  const res = await fetch(`${API_BASE}api/login`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(data),
  });
  const json = await res.json();
  return json;
}

/**
 * ログアウト
 * @param {*} access_token 
 * @returns 
 */
export async function fetchLogout(access_token) {
  const res = await fetch(`${API_BASE}api/logout`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${access_token}`,
    },
  });
  const json = await res.json();
  return json;
}

/**
 * ログインユーザ情報取得
 * @param {*} access_token 
 * @returns 
 */
export async function fetchGetUser(access_token) {
  const res = await authFetch(`${API_BASE}api/users`, {
    headers: {
      Authorization: `Bearer ${access_token}`,
    },
  });
  if (res.status === 401) {
    return { _unauthorized: true };
  }
  if (!res.ok) {
    throw new Error(`HTTP ${res.status}`);
  }
  const json = await res.json();
  return json;
}

/**
 * パスワード更新
 * @param {*} access_token 
 * @param {*} data 
 * @returns 
 */
export async function fetchUpdatePassword(access_token, data) {
  const res = await authFetch(`${API_BASE}api/users/update-password`, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${access_token}`,
    },
    body: JSON.stringify(data),
  });

  const json = await res.json();
  return json;
}

/**
 * パスワード再設定メール送信
 * @param {*} data 
 * @returns 
 */
export async function fetchRequestResetPassword(data) {
  const res = await fetch(`${API_BASE}api/request-reset-password`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(data),
  });
  const json = await res.json();
  return json;
}

/**
 * パスワード再設定
 * @param {*} data 
 * @returns 
 */
export async function fetchResetPassword(data) {
  const res = await fetch(`${API_BASE}api/reset-password`, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(data),
  });
  const json = await res.json();
  return json;
}

/**
 * メールアドレス変更メール送信
 * @param {*} access_token 
 * @param {*} data 
 * @returns 
 */
export async function fetchRequestChangeEmail(access_token, data) {
  const res = await authFetch(`${API_BASE}api/request-change-email`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${access_token}`,
    },
    body: JSON.stringify(data),
  });
  const json = await res.json();
  return json;
}

/**
 * メールアドレス変更
 * @param {*} access_token 
 * @param {*} data 
 * @returns 
 */
export async function fetchChangeEmail(access_token, data) {
  const res = await fetch(`${API_BASE}api/change-email`, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${access_token}`,
    },
    body: JSON.stringify(data),
  });
  const json = await res.json();
  return json;
}

/**
 * ユーザ削除
 * @param {*} access_token 
 * @returns 
 */
export async function fetchDeleteUser(access_token) {
  const res = await authFetch(`${API_BASE}api/users`, {
    method: "DELETE",
    headers: {
      Authorization: `Bearer ${access_token}`,
    },
  });
  const json = await res.json();
  return json;
}

/**
 * 顧客情報取得
 * @param {*} access_token 
 * @returns
 */
export async function fetchGetCustomer(access_token) {
  const res = await authFetch(`${API_BASE}api/customers`, {
    headers: {
      Authorization: `Bearer ${access_token}`,
    },
  });
  const json = await res.json();
  return json;
}

/**
 * 新規顧客新規登録
 * @param {*} access_token 
 * @param {*} data 
 * @returns 
 */
export async function fetchRegisterNewCustomer(access_token, data) {
  const res = await fetch(`${API_BASE}api/customers/register-new-customer`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${access_token}`,
    },
    body: JSON.stringify(data),
  });
  const json = await res.json();
  return json;
}

/**
 * 既存顧客新規登録
 * @param {*} access_token 
 * @param {*} data 
 * @returns 
 */
export async function fetchRegisterExistingCustomer(access_token, data) {
  const res = await fetch(`${API_BASE}api/customers/register-existing-customer`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${access_token}`,
    },
    body: JSON.stringify(data),
  });
  const json = await res.json();
  return json;
}

/**
 * 顧客情報更新
 * @param {*} access_token 
 * @param {*} data 
 * @returns 
 */
export async function fetchUpdateCustomer(access_token, data) {
  const res = await authFetch(`${API_BASE}api/customers`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${access_token}`,
    },
    body: JSON.stringify(data),
  });
  const json = await res.json();
  return json;
}

/**
 * クレジットカード有効期限更新
 * @param {*} access_token 
 * @param {*} data 
 * @returns 
 */
export async function fetchUpdateCardExpiry(access_token, data) {
  const res = await authFetch(`${API_BASE}api/customers/update-card-expiry`, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${access_token}`,
    },
    body: JSON.stringify(data),
  });
  const json = await res.json();
  return json;
}

/**
 * 問い合わせ送信
 * @param {*} access_token 
 * @param {*} data 
 * @returns 
 */
export async function fetchReceiveInquiry(access_token, data) {
  const res = await authFetch(`${API_BASE}api/customers/receive-inquiry`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${access_token}`,
    },
    body: JSON.stringify(data),
  });
  const json = await res.json();
  return json;
}

/**
 * 受注情報取得
 * @param {*} access_token 
 * @returns 
 */
export async function fetchGetOrders(access_token) {
  const res = await authFetch(`${API_BASE}api/orders`, {
    headers: {
      Authorization: `Bearer ${access_token}`,
    },
  });
  const json = await res.json();
  return json;
}

/**
 * 受注情報取得
 * @param {*} order_number 
 * @param {*} access_token 
 * @returns 
 */
export async function fetchGetOrder(order_number, access_token) {
  const res = await authFetch(
    `${API_BASE}api/orders/${order_number}`, {
      headers: {
        Authorization: `Bearer ${access_token}`,
      },
    }
  );
  const json = await res.json();
  return json;
}

/**
 * ポイント履歴取得
 * @param {*} access_token 
 * @returns 
 */
export async function fetchGetPointHistories(access_token) {
  const res = await authFetch(
    `${API_BASE}api/point-histories`, {
      headers: {
        Authorization: `Bearer ${access_token}`,
      },
    }
  );
  const json = await res.json();
  return json;
}

/**
 * 定期情報取得
 * @param {*} access_token 
 * @returns 
 */
export async function fetchGetSubscriptions(access_token) {
  const res = await authFetch(`${API_BASE}api/subscriptions`, {
    headers: {
      Authorization: `Bearer ${access_token}`,
    },
  });
  const json = await res.json();
  return json;
}

/**
 * 定期情報取得
 * @param {*} subscription_number 
 * @param {*} access_token 
 * @returns 
 */
export async function fetchGetSubscription(
  subscription_number,
  access_token
) {
  const res = await authFetch(
    `${API_BASE}api/subscriptions/${subscription_number}`, {
      headers: {
        Authorization: `Bearer ${access_token}`,
      },
    }
  );
  const json = await res.json();
  return json;
}

/**
 * 定期次回使用ポイント変更
 * @param {*} subscription_number 
 * @param {*} access_token 
 * @param {*} data 
 * @returns 
 */
export async function fetchUpdateUsagePoints(subscription_number, access_token, data) {
  const res = await authFetch(`${API_BASE}api/subscriptions/${subscription_number}/update-usage-points`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${access_token}`,
    },
    body: JSON.stringify(data),
  });
  const json = await res.json();
  return json;
}
